import { create } from 'zustand'
import type { Conversation, Message } from '@/schemas/chat'
import { chatService } from '@/services/chat'

interface ChatState {
  // Conversation list
  conversations: Conversation[]
  isLoadingConversations: boolean

  // Active conversation
  activeConversation: Conversation | null
  messages: Message[]
  isLoadingMessages: boolean

  // Sending state
  isSending: boolean
  error: string | null

  // Actions
  fetchConversations: (projectId: string) => Promise<void>
  createConversation: (projectId: string, agentId: string, title?: string) => Promise<Conversation | null>
  selectConversation: (projectId: string, conversationId: string) => Promise<void>
  sendMessage: (projectId: string, content: string) => Promise<void>
  deleteConversation: (projectId: string, conversationId: string) => Promise<void>
  clearError: () => void
  reset: () => void
}

const initialState = {
  conversations: [] as Conversation[],
  isLoadingConversations: false,
  activeConversation: null as Conversation | null,
  messages: [] as Message[],
  isLoadingMessages: false,
  isSending: false,
  error: null as string | null,
}

export const useChatStore = create<ChatState>()((set, get) => ({
  ...initialState,

  fetchConversations: async (projectId: string) => {
    set({ isLoadingConversations: true })
    try {
      const response = await chatService.listConversations(projectId, {
        page: 1,
        page_size: 100,
      })
      set({ conversations: response.conversations, isLoadingConversations: false })
    } catch (error) {
      console.error('Failed to fetch conversations:', error)
      set({ isLoadingConversations: false })
    }
  },

  createConversation: async (projectId: string, agentId: string, title?: string) => {
    try {
      const conversation = await chatService.createConversation(projectId, {
        agent_id: agentId,
        title,
      })
      set((state) => ({
        conversations: [conversation, ...state.conversations],
        activeConversation: conversation,
        messages: [],
      }))
      return conversation
    } catch (error) {
      const errorMessage = extractErrorMessage(error, 'Erro ao criar conversa')
      set({ error: errorMessage })
      return null
    }
  },

  selectConversation: async (projectId: string, conversationId: string) => {
    set({ isLoadingMessages: true, error: null })
    try {
      const detail = await chatService.getConversation(projectId, conversationId)
      set({
        activeConversation: detail,
        messages: detail.messages,
        isLoadingMessages: false,
      })
    } catch (error) {
      const errorMessage = extractErrorMessage(error, 'Erro ao carregar conversa')
      set({ error: errorMessage, isLoadingMessages: false })
    }
  },

  sendMessage: async (projectId: string, content: string) => {
    const { activeConversation } = get()
    if (!activeConversation) return

    // Optimistic user message
    const optimisticMessage: Message = {
      id: crypto.randomUUID(),
      conversation_id: activeConversation.id,
      role: 'user',
      content,
      metadata_: {},
      created_at: new Date().toISOString(),
    }

    set((state) => ({
      messages: [...state.messages, optimisticMessage],
      isSending: true,
      error: null,
    }))

    try {
      const response = await chatService.sendMessage(
        projectId,
        activeConversation.id,
        { content }
      )

      // Replace optimistic message with real one and add assistant message
      set((state) => ({
        messages: [
          ...state.messages.filter((m) => m.id !== optimisticMessage.id),
          response.user_message,
          response.assistant_message,
        ],
        isSending: false,
        // Update conversation title if it was auto-set
        activeConversation: state.activeConversation
          ? {
              ...state.activeConversation,
              title: state.activeConversation.title || response.user_message.content.slice(0, 50) + (response.user_message.content.length > 50 ? '...' : ''),
            }
          : null,
        // Update conversations list title too
        conversations: state.conversations.map((c) =>
          c.id === activeConversation.id
            ? { ...c, title: c.title || response.user_message.content.slice(0, 50) + (response.user_message.content.length > 50 ? '...' : '') }
            : c
        ),
      }))
    } catch (error) {
      const errorMessage = extractErrorMessage(error, 'Erro ao enviar mensagem')
      set({
        error: errorMessage,
        isSending: false,
      })
    }
  },

  deleteConversation: async (projectId: string, conversationId: string) => {
    try {
      await chatService.deleteConversation(projectId, conversationId)
      set((state) => ({
        conversations: state.conversations.filter((c) => c.id !== conversationId),
        activeConversation:
          state.activeConversation?.id === conversationId
            ? null
            : state.activeConversation,
        messages:
          state.activeConversation?.id === conversationId ? [] : state.messages,
      }))
    } catch (error) {
      const errorMessage = extractErrorMessage(error, 'Erro ao excluir conversa')
      set({ error: errorMessage })
    }
  },

  clearError: () => set({ error: null }),

  reset: () => set(initialState),
}))

function extractErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error) {
    return error.message
  }
  if (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof (error as { response?: { data?: { detail?: string } } }).response?.data
      ?.detail === 'string'
  ) {
    return (error as { response: { data: { detail: string } } }).response.data.detail
  }
  return fallback
}
